/**
 * The matter timeline.
 *
 * A timeline entry stores NO WORDING. The text renders from the Event Registry at
 * read time, which is what makes an event type with no client wording structurally
 * incapable of reaching a client (BR-26). `description` on this staff endpoint is
 * always the INTERNAL rendering — the portal builds its own, from its own function,
 * on a serializer that is registered on the portal root only.
 *
 * "Preview as client" is a first-class control on every screen that can publish
 * (screens doc §7.1), and this is one of them. Here it filters to the entries a
 * client could see. It is a filter over what is already loaded, so it is honest about
 * WHICH entries, but it does NOT re-render their wording — the client would see the
 * registry's client text, not this internal text. That is stated on screen rather
 * than glossed over, because a preview that quietly showed internal wording as if it
 * were client wording would be worse than no preview.
 */

import { qk, useInfiniteList, type TimelineEntry } from '@legalcrm/api';
import { Badge, Button, Card, ErrorState, Skeleton, Timeline, cn } from '@legalcrm/ui';
import { useState } from 'react';

import { STAFF } from '@legalcrm/api';
import { formatDateTime } from '@/lib/format';

export function CaseTimelineTab({ caseId }: { caseId: string }) {
  const [clientView, setClientView] = useState(false);

  const list = useInfiniteList<TimelineEntry>(
    qk.sub('cases', caseId, 'timeline'),
    STAFF.caseTimeline(caseId),
  );

  const entries = clientView
    ? list.items.filter((entry) => entry.visibility === 'client')
    : list.items;

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <h3 className="text-lg font-semibold text-ink">Activity</h3>
          <p className="text-sm text-ink-muted">
            {clientView
              ? 'Only entries the client could see.'
              : 'Everything recorded on this matter.'}
          </p>
        </div>
        <Button
          size="sm"
          variant={clientView ? 'primary' : 'secondary'}
          onClick={() => setClientView((current) => !current)}
          aria-pressed={clientView}
        >
          {clientView ? 'Showing client view' : 'Preview as client'}
        </Button>
      </div>

      {clientView && (
        <div className="border-b border-client-facing/30 bg-client-facing-subtle px-4 py-2">
          <p className="text-sm text-ink">
            <strong className="font-semibold">Client view.</strong> These are the entries a
            client could see. The wording they read is generated separately for them and may
            differ from the internal text below.
          </p>
        </div>
      )}

      <div className="px-4 py-4">
        {list.isLoading && (
          <div className="flex flex-col gap-3" aria-busy="true">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        )}

        {Boolean(list.error) && (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} size="sm" />
        )}

        {!list.isLoading && !list.error && (
          <Timeline
            items={entries.map((entry) => ({
              id: entry.id,
              description: entry.description,
              timestamp: entry.occurred_at,
              // Rendered with a persistent text label as well as a colour. CR-25 is
              // the leak class where somebody believed an entry was internal.
              visibility: entry.visibility,
            }))}
            formatTimestamp={formatDateTime}
            emptyMessage={
              clientView
                ? 'Nothing on this matter is visible to the client yet.'
                : 'Nothing has been recorded on this matter yet.'
            }
          />
        )}

        {list.hasNextPage && (
          <div className={cn('mt-3 flex justify-center')}>
            <Button
              size="sm"
              variant="secondary"
              loading={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              Load earlier activity
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/** Shown beside the tab label. */
export function TimelineVisibilityLegend() {
  return (
    <div className="flex items-center gap-2">
      <Badge tone="internal" size="sm">
        Internal
      </Badge>
      <Badge tone="client-facing" size="sm">
        Client can see this
      </Badge>
    </div>
  );
}
